import AppsIcon from '@material-design-icons/svg/filled/apps.svg?react';
import BusinessIcon from '@material-design-icons/svg/filled/business.svg?react';
import CloseIcon from '@material-design-icons/svg/filled/close.svg?react';
import EventIcon from '@material-design-icons/svg/filled/event.svg?react';
import NotificationsIcon from '@material-design-icons/svg/filled/notifications.svg?react';
import OpenInNewIcon from '@material-design-icons/svg/filled/open_in_new.svg?react';
import PersonIcon from '@material-design-icons/svg/filled/person.svg?react';
import VerifiedIcon from '@material-design-icons/svg/filled/verified.svg?react';
import type { inferRouterOutputs } from '@trpc/server';
import { isLeft, isRight } from 'fp-ts/Either';
import { use, useEffect } from 'react';
import type { KybStatus } from '../../../api/src/types/database';
import { TRPCContext } from '../contexts/TRPC';
import { useAsyncData } from '../hooks/useAsyncData';
import type { Router } from '../types/TRPC';
import { getErrorMessage } from '../utils/errorHandling';
import { captureAndReportError } from '../utils/errorReporting';
import { Button } from './Button';
import { TypedStatusBadge } from './common/StatusBadge';
import { KybEventTimeline } from './KybEventTimeline';

type RouterOutputs = inferRouterOutputs<Router>;
type BaseKybSubmissionDetail = Extract<
  RouterOutputs['getKybSubmissionDetail'],
  { _tag: 'Right' }
>['right'];
type KybSubmissionDetailResult = BaseKybSubmissionDetail & {
  syncStatus?: {
    lastSyncTimestamp?: string;
    lastSyncStatus?: string;
    syncHealth: 'healthy' | 'warning' | 'error' | 'unknown';
    errorMessage?: string;
    metadata?: unknown;
  };
};

type SyncMetadata = {
  itemsProcessed?: number;
  errors?: number;
  skipped?: number;
  [key: string]: unknown;
};

function isSyncMetadata(value: unknown): value is SyncMetadata {
  return typeof value === 'object' && value !== null;
}

function getSyncMetric(value: unknown): number {
  return typeof value === 'number' ? value : 0;
}

interface KybSubmissionDetailProps {
  submissionId: string;
  onClose: () => void;
}

export function KybSubmissionDetail({
  submissionId,
  onClose,
}: KybSubmissionDetailProps) {
  const trpcContext = use(TRPCContext);
  const { data, isLoading, setIsLoading, error, setError, run } =
    useAsyncData<KybSubmissionDetailResult | null>({
      initialData: null,
      initialLoading: true,
    });

  useEffect(() => {
    if (!trpcContext || !trpcContext.client) {
      setError('TRPC context not initialized');
      setIsLoading(false);
      return;
    }

    void run(
      () =>
        trpcContext.client.getKybSubmissionDetail.query({
          submissionId,
        }),
      {
        onSuccess: (result, helpers) => {
          if (isRight(result)) {
            helpers.setData(result.right);
          } else if (isLeft(result)) {
            helpers.setError(
              result.left.message || 'Failed to fetch submission details',
            );
          }
        },
        onError: (err, helpers) => {
          captureAndReportError('Error fetching KYB submission detail:', err);
          helpers.setError(getErrorMessage(err));
        },
      },
    );
  }, [run, setError, setIsLoading, trpcContext, submissionId]);

  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleEscape]);

  if (!data && !isLoading) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-neutral-900 rounded-2xl border border-neutral-700 max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-neutral-700 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">
            KYB Submission Details
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-neutral-800 rounded-lg transition-colors"
          >
            <CloseIcon className="size-5 text-neutral-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="p-6 text-center text-neutral-400">
              Loading details…
            </div>
          ) : error ? (
            <div className="p-6 text-center text-red-400">
              Error loading details: {error}
            </div>
          ) : data ? (
            <div className="p-6 space-y-6">
              <div className="bg-neutral-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-white">
                    Submission Status
                  </h3>
                  <TypedStatusBadge
                    type="kyb"
                    status={data.status as KybStatus}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-neutral-400">Submitted</div>
                    <div className="text-white">
                      {data.submittedAt
                        ? new Date(data.submittedAt).toLocaleString()
                        : 'Not submitted'}
                    </div>
                  </div>
                  <div>
                    <div className="text-neutral-400">Review Started</div>
                    <div className="text-white">
                      {data.reviewStartedAt
                        ? new Date(data.reviewStartedAt).toLocaleString()
                        : 'Not started'}
                    </div>
                  </div>
                  <div>
                    <div className="text-neutral-400">Review Completed</div>
                    <div className="text-white">
                      {data.reviewCompletedAt
                        ? new Date(data.reviewCompletedAt).toLocaleString()
                        : 'Not completed'}
                    </div>
                  </div>
                </div>
                {data.rejectionReason && (
                  <div className="mt-4 p-3 bg-red-900/20 rounded-lg border border-red-600/30">
                    <div className="text-sm text-red-400 font-medium">
                      Rejection Reason
                    </div>
                    <div className="text-sm text-red-300 mt-1">
                      {data.rejectionReason}
                    </div>
                  </div>
                )}
                {data.additionalInfoRequested && (
                  <div className="mt-4 p-3 bg-yellow-900/20 rounded-lg border border-yellow-600/30">
                    <div className="text-sm text-yellow-400 font-medium">
                      Additional Information Requested
                    </div>
                    <div className="text-sm text-yellow-300 mt-1">
                      {data.additionalInfoRequested}
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-neutral-800 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-4">
                  <PersonIcon className="size-5 text-white" />
                  <h3 className="text-lg font-medium text-white">
                    User Information
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-neutral-400">Full Name</div>
                    <div className="text-white">{data.user.fullName}</div>
                  </div>
                  <div>
                    <div className="text-neutral-400">Email</div>
                    <div className="text-white">{data.user.email}</div>
                  </div>
                  <div>
                    <div className="text-neutral-400">User ID</div>
                    <div className="text-white font-mono text-xs">
                      {data.user.id}
                    </div>
                  </div>
                  <div>
                    <div className="text-neutral-400">Onboarding State</div>
                    <div className="text-white">
                      {data.user.onboardingState}
                    </div>
                  </div>
                  <div>
                    <div className="text-neutral-400">Email Verified</div>
                    <div className="text-white">
                      {data.user.emailVerified ? 'Yes' : 'No'}
                    </div>
                  </div>
                  <div>
                    <div className="text-neutral-400">Account Created</div>
                    <div className="text-white">
                      {new Date(data.user.created).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-neutral-800 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-4">
                  <BusinessIcon className="size-5 text-white" />
                  <h3 className="text-lg font-medium text-white">
                    Publisher Information
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-neutral-400">Type</div>
                    <div className="text-white">{data.publisher.type}</div>
                  </div>
                  <div>
                    <div className="text-neutral-400">Name</div>
                    <div className="text-white">{data.publisher.name}</div>
                  </div>
                  {data.publisher.website && (
                    <div>
                      <div className="text-neutral-400">Website</div>
                      <a
                        href={data.publisher.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-teal-400 hover:text-teal-300"
                      >
                        {data.publisher.website}
                      </a>
                    </div>
                  )}
                  {data.publisher.walletAddress && (
                    <div>
                      <div className="text-neutral-400">Wallet Address</div>
                      <div className="text-white font-mono text-xs break-all">
                        {data.publisher.walletAddress}
                      </div>
                    </div>
                  )}
                  {data.publisher.address && (
                    <div className="md:col-span-2">
                      <div className="text-neutral-400">Address</div>
                      <div className="text-white">{data.publisher.address}</div>
                    </div>
                  )}
                </div>
              </div>

              {data.syncStatus && (
                <div className="bg-neutral-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-white">
                      External Sync Status
                    </h3>
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-3 h-3 rounded-full ${
                          data.syncStatus.syncHealth === 'healthy'
                            ? 'bg-green-500'
                            : data.syncStatus.syncHealth === 'warning'
                              ? 'bg-yellow-500'
                              : data.syncStatus.syncHealth === 'error'
                                ? 'bg-red-500'
                                : 'bg-neutral-500'
                        }`}
                      />
                      <span className="text-sm text-neutral-300 capitalize">
                        {data.syncStatus.syncHealth}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-neutral-400">Last Sync</div>
                      <div className="text-white">
                        {data.syncStatus.lastSyncTimestamp
                          ? new Date(
                              data.syncStatus.lastSyncTimestamp,
                            ).toLocaleString()
                          : 'Never'}
                      </div>
                    </div>
                    <div>
                      <div className="text-neutral-400">Sync Status</div>
                      <div className="text-white">
                        {data.syncStatus.lastSyncStatus || 'Unknown'}
                      </div>
                    </div>
                  </div>
                  {data.syncStatus.errorMessage && (
                    <div className="mt-4 p-3 bg-red-900/20 rounded-lg border border-red-600/30">
                      <div className="text-sm text-red-400 font-medium">
                        Sync Error
                      </div>
                      <div className="text-sm text-red-300 mt-1">
                        {data.syncStatus.errorMessage}
                      </div>
                    </div>
                  )}
                  {isSyncMetadata(data.syncStatus.metadata) && (
                    <div className="mt-4">
                      <div className="text-sm text-neutral-400 mb-2">
                        Sync Metadata
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div className="bg-neutral-900 rounded-lg p-3">
                          <div className="text-neutral-400">
                            Items Processed
                          </div>
                          <div className="text-white font-medium">
                            {getSyncMetric(
                              data.syncStatus.metadata.itemsProcessed,
                            )}
                          </div>
                        </div>
                        <div className="bg-neutral-900 rounded-lg p-3">
                          <div className="text-neutral-400">Errors</div>
                          <div className="text-white font-medium">
                            {getSyncMetric(data.syncStatus.metadata.errors)}
                          </div>
                        </div>
                        <div className="bg-neutral-900 rounded-lg p-3">
                          <div className="text-neutral-400">Skipped</div>
                          <div className="text-white font-medium">
                            {getSyncMetric(data.syncStatus.metadata.skipped)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {data.sumsubApplicant && (
                <div className="bg-neutral-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <VerifiedIcon className="size-5 text-white" />
                      <h3 className="text-lg font-medium text-white">
                        Sumsub Verification Details
                      </h3>
                    </div>
                    {data.sumsubApplicant.sumsubId && (
                      <a
                        href={`https://cockpit.sumsub.com/checkus/applicant/${data.sumsubApplicant.sumsubId}/basicInfo?clientId=solana`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm text-teal-400 hover:text-teal-300 border border-teal-400/30 hover:border-teal-400/50 rounded-lg transition-colors"
                      >
                        <OpenInNewIcon className="size-4 fill-current" />
                        <span>View in Sumsub Dashboard</span>
                      </a>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-neutral-400">Applicant ID</div>
                      <div className="text-white font-mono text-xs">
                        {data.sumsubApplicant.sumsubId}
                      </div>
                    </div>
                    <div>
                      <div className="text-neutral-400">Review Status</div>
                      <div className="text-white">
                        {data.sumsubApplicant.reviewStatus || 'Pending'}
                      </div>
                    </div>
                    {data.sumsubApplicant.externalUserId && (
                      <div>
                        <div className="text-neutral-400">External User ID</div>
                        <div className="text-white font-mono text-xs">
                          {data.sumsubApplicant.externalUserId}
                        </div>
                      </div>
                    )}
                    {data.sumsubApplicant.inspectionId && (
                      <div>
                        <div className="text-neutral-400">Inspection ID</div>
                        <div className="text-white font-mono text-xs">
                          {data.sumsubApplicant.inspectionId}
                        </div>
                      </div>
                    )}
                    {data.sumsubApplicant.correlationId && (
                      <div>
                        <div className="text-neutral-400">Correlation ID</div>
                        <div className="text-white font-mono text-xs">
                          {data.sumsubApplicant.correlationId}
                        </div>
                      </div>
                    )}
                    <div>
                      <div className="text-neutral-400">Type</div>
                      <div className="text-white">
                        {data.sumsubApplicant.type || 'N/A'}
                      </div>
                    </div>
                    {data.sumsubApplicant.companyName && (
                      <div>
                        <div className="text-neutral-400">Company Name</div>
                        <div className="text-white">
                          {data.sumsubApplicant.companyName}
                        </div>
                      </div>
                    )}
                    {data.sumsubApplicant.email && (
                      <div>
                        <div className="text-neutral-400">Sumsub Email</div>
                        <div className="text-white">
                          {data.sumsubApplicant.email}
                        </div>
                      </div>
                    )}
                    {data.sumsubApplicant.phone && (
                      <div>
                        <div className="text-neutral-400">Phone</div>
                        <div className="text-white">
                          {data.sumsubApplicant.phone}
                        </div>
                      </div>
                    )}
                    {data.sumsubApplicant.sumsubCreatedAt && (
                      <div>
                        <div className="text-neutral-400">
                          Created at Sumsub
                        </div>
                        <div className="text-white">
                          {new Date(
                            data.sumsubApplicant.sumsubCreatedAt,
                          ).toLocaleString()}
                        </div>
                      </div>
                    )}
                    {data.sumsubApplicant.sumsubUpdatedAt && (
                      <div>
                        <div className="text-neutral-400">
                          Updated at Sumsub
                        </div>
                        <div className="text-white">
                          {new Date(
                            data.sumsubApplicant.sumsubUpdatedAt,
                          ).toLocaleString()}
                        </div>
                      </div>
                    )}
                    <div>
                      <div className="text-neutral-400">Last Synced</div>
                      <div className="text-white">
                        {data.sumsubApplicant.lastSyncedAt
                          ? new Date(
                              data.sumsubApplicant.lastSyncedAt,
                            ).toLocaleString()
                          : 'Never'}
                      </div>
                    </div>
                  </div>
                  {data.sumsubApplicant.properties && (
                    <div className="mt-4">
                      <div className="text-sm text-neutral-400 mb-2">
                        Additional Properties
                      </div>
                      <pre className="bg-neutral-900 rounded-lg p-3 text-xs text-neutral-300 overflow-x-auto max-h-32">
                        {JSON.stringify(
                          data.sumsubApplicant.properties,
                          null,
                          2,
                        )}
                      </pre>
                    </div>
                  )}
                  {data.sumsubApplicant.reviewResult && (
                    <div className="mt-4">
                      <div className="text-sm text-neutral-400 mb-2">
                        Review Result
                      </div>
                      <pre className="bg-neutral-900 rounded-lg p-3 text-xs text-neutral-300 overflow-x-auto max-h-48">
                        {JSON.stringify(
                          data.sumsubApplicant.reviewResult,
                          null,
                          2,
                        )}
                      </pre>
                    </div>
                  )}
                </div>
              )}

              {data.relatedDapps.length > 0 && (
                <div className="bg-neutral-800 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <AppsIcon className="size-5 text-white" />
                    <h3 className="text-lg font-medium text-white">
                      Related Dapps
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {data.relatedDapps.map(
                      (
                        dapp: KybSubmissionDetailResult['relatedDapps'][number],
                      ) => (
                        <div
                          key={dapp.id}
                          className="flex items-center justify-between p-3 bg-neutral-900 rounded-lg"
                        >
                          <div>
                            <div className="text-sm text-white font-medium">
                              {dapp.dappName}
                            </div>
                            <div className="text-xs text-neutral-400">
                              {dapp.androidPackage || 'No package'}
                            </div>
                          </div>
                          <div className="text-sm text-neutral-400">
                            {dapp.status}
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}

              {data.events.length > 0 && (
                <div className="bg-neutral-800 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <EventIcon className="size-5 text-white" />
                    <h3 className="text-lg font-medium text-white">
                      Event Timeline
                    </h3>
                  </div>
                  <KybEventTimeline
                    events={data.events.map((event) => ({
                      ...event,
                      payload: event.payload ?? {},
                    }))}
                  />
                </div>
              )}

              {data.alerts.length > 0 && (
                <div className="bg-neutral-800 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <NotificationsIcon className="size-5 text-white" />
                    <h3 className="text-lg font-medium text-white">Alerts</h3>
                  </div>
                  <div className="space-y-2">
                    {data.alerts.map(
                      (alert: KybSubmissionDetailResult['alerts'][number]) => (
                        <div
                          key={alert.id}
                          className="p-3 bg-neutral-900 rounded-lg text-sm"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="text-white font-medium">
                              {alert.title}
                            </div>
                            <div className="text-xs text-neutral-400">
                              {new Date(alert.created).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="text-neutral-300">
                            {alert.message}
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-xs text-neutral-400">
                            <span>Status: {alert.status}</span>
                            {alert.emailSentAt && <span>Email sent</span>}
                            {alert.readAt && <span>Read</span>}
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>

        <div className="px-6 py-4 border-t border-neutral-700 flex items-center justify-between">
          <div className="text-sm text-neutral-400">
            Submission ID: {submissionId}
          </div>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
